import { useRef, useState } from 'react';
import { useTranslations } from 'use-intl';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import {
  Body,
  Button,
  Card,
  Eyebrow,
  Field,
  Heading,
  I,
  PageHeader,
  Row,
  Screen,
  ScreenBody,
  Select,
  Stack,
} from '@leanin/ui';
import { REGIONS, type Region } from '@leanin/shared/region';
import { GROUP_ACCESS_TIERS, type GroupAccess } from '@leanin/shared/views/event';
import { RouterLinkAs } from '@/lib/router-link';
import { BodyWithAside } from '@/components/page-shell';
import {
  AdminImagePanel,
  type AdminImagePanelHandle,
} from '@/components/group/admin/AdminImagePanel';
import {
  NetworkProfileFields,
  EMPTY_SOCIAL_DRAFT,
  socialDraftToPayload,
  type NetworkProfileDraft,
} from '@/components/networks/admin/NetworkProfileFields';
import { useCreateNetwork } from '@/lib/mutations/groups';
import { requirePlatformRole } from '@/lib/route-guards';
import { failureFromError } from '@/lib/api-error';

// Platform-admin-only network provisioning. The `_authed` parent guarantees a
// session; here we narrow to the `admin` role and bounce everyone else back to
// the directory. Network leaders apply via /networks/new (the Network Leaders
// program); this surface is the staff tool that mints the actual page.
export const Route = createFileRoute('/_authed/networks/create')({
  beforeLoad: ({ context }) => {
    requirePlatformRole(context, {
      role: 'admin',
      bounceTo: () => redirect({ to: '/networks' }),
    });
  },
  component: NetworkCreatePage,
});

// Every tier a Network can carry. `network` scopes a Circle to its parent
// Network, so it has no meaning here; `network_leaders` is admin-only to set,
// and this whole route is admin-only.
type Access = Exclude<GroupAccess, 'network'>;

const ACCESS_VALUES: ReadonlyArray<Access> = GROUP_ACCESS_TIERS.filter(
  (a): a is Access => a !== 'network',
);

const EMPTY_DRAFT: NetworkProfileDraft = {
  name: '',
  tagline: '',
  region: REGIONS[0] as Region,
  mission: '',
  description: '',
  welcomeMessage: '',
  joinInstructions: '',
  tags: [],
  social: EMPTY_SOCIAL_DRAFT,
};

const TIP_KEYS = ['nameConsistently', 'convert', 'editLater', 'visibility'] as const;

function NetworkCreatePage() {
  const tn = useTranslations('Networks');
  const navigate = useNavigate();

  const accessOptions = ACCESS_VALUES.map((value) => ({
    value,
    label: tn(`create.access.${value}`),
  }));
  const tips = TIP_KEYS.map((key) => ({
    title: tn(`create.tips.${key}.title`),
    body: tn(`create.tips.${key}.body`),
  }));
  const createNetwork = useCreateNetwork();

  const [draft, setDraft] = useState<NetworkProfileDraft>(EMPTY_DRAFT);
  const [access, setAccess] = useState<Access>('public');

  const imagesRef = useRef<AdminImagePanelHandle | null>(null);
  const [imagesPending, setImagesPending] = useState(false);
  const [committingImages, setCommittingImages] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const patch = (p: Partial<NetworkProfileDraft>) =>
    setDraft((d) => ({ ...d, ...p }));

  // Server bounds: name min 3, description min 1.
  const canSubmit =
    draft.name.trim().length >= 3 && draft.description.trim().length >= 1;

  const isPending = createNetwork.isPending || committingImages;

  const onSubmit = async () => {
    setSubmitError(null);
    if (!canSubmit) {
      setSubmitError(tn('create.errors.validation'));
      return;
    }

    const socials = socialDraftToPayload(draft.social);
    let detail;
    try {
      detail = await createNetwork.mutateAsync({
        name: draft.name.trim(),
        description: draft.description.trim(),
        region: draft.region,
        access,
        tagline: draft.tagline.trim() || undefined,
        mission: draft.mission.trim() || undefined,
        welcomeMessage: draft.welcomeMessage.trim() || undefined,
        joinInstructions: draft.joinInstructions.trim() || undefined,
        tags: draft.tags.length > 0 ? draft.tags : undefined,
        socialLinks: Object.keys(socials).length > 0 ? socials : undefined,
      });
    } catch (err) {
      setSubmitError(
        failureFromError(err, tn('create.errors.createFailed')).message,
      );
      return;
    }

    // Images are optional and committed under the freshly-created id. A failed
    // upload doesn't strand the admin — the Network exists; surface the
    // message and route forward so they can retry from the admin Overview.
    if (imagesPending) {
      setCommittingImages(true);
      try {
        await imagesRef.current?.commit(detail.id);
      } catch (err) {
        setSubmitError(
          failureFromError(
            err,
            tn('create.errors.imageUploadFailed'),
          ).message,
        );
      } finally {
        setCommittingImages(false);
      }
    }

    navigate({ to: '/networks/$id', params: { id: detail.id } });
  };

  return (
    <Screen>
      <ScreenBody gap="xl">
        <PageHeader
          eyebrow={tn('create.eyebrow')}
          title={tn('create.title')}
          subtitle={tn('create.subtitle')}
          back={{ label: tn('create.back'), href: '/networks' }}
          actions={
            <>
              <Button href="/networks" linkAs={RouterLinkAs} variant="ghost">
                {tn('create.cancel')}
              </Button>
              <Button
                variant="primary"
                iconRight={I.arrow}
                onClick={onSubmit}
                disabled={!canSubmit}
                loading={isPending}
              >
                {tn('create.publish')}
              </Button>
            </>
          }
        />
        <BodyWithAside
          asideWidth={380}
          aside={
            <Card pad="default">
              <Stack gap="lg">
                <Stack gap="xs">
                  <Eyebrow tone="accent">{tn('create.asideEyebrow')}</Eyebrow>
                  <Heading level={3}>{tn('create.asideTitle')}</Heading>
                </Stack>
                <Stack gap="md">
                  {tips.map((tip) => (
                    <Stack key={tip.title} gap="xs">
                      <Heading level={5}>{tip.title}</Heading>
                      <Body size="sm" color="mute">
                        {tip.body}
                      </Body>
                    </Stack>
                  ))}
                </Stack>
              </Stack>
            </Card>
          }
        >
          <Stack gap="lg">
            <Card pad="flush">
              <AdminImagePanel
                ref={imagesRef}
                groupKind="network"
                // Placeholder id — AdminImagePanel doesn't presign until
                // commit(), and Publish passes the new Network's id as the
                // override at that point.
                groupId="pending"
                groupName={draft.name || tn('create.networkNameFallback')}
                currentCoverUrl={null}
                currentLogoUrl={null}
                onPendingChange={setImagesPending}
              />
              <NetworkProfileFields value={draft} onChange={patch} />
            </Card>

            <Card pad="spacious">
              <Stack gap="lg">
                <Stack gap="xs">
                  <Eyebrow tone="accent">{tn('create.visibility')}</Eyebrow>
                  <Body size="sm" color="mute">
                    {tn('create.visibilityHelp')}
                  </Body>
                </Stack>
                <Row gap="md" wrap align="flex-start">
                  <Stack gap="sm" style={{ flex: '1 1 240px', minWidth: 0 }}>
                    <Field label={tn('create.directoryListing')}>
                      <Select
                        value={access}
                        options={accessOptions}
                        onChange={(e) => setAccess(e.target.value as Access)}
                      />
                    </Field>
                  </Stack>
                </Row>
              </Stack>
            </Card>

            {submitError !== null ? (
              <Card pad="default" tone="burgundy">
                <Body color="primary">{submitError}</Body>
              </Card>
            ) : null}

            <Row justify="space-between" align="center">
              <Body size="sm" color="mute">
                {tn('create.editLaterNote')}
              </Body>
              <Row gap="sm">
                <Button href="/networks" linkAs={RouterLinkAs} variant="ghost">
                  {tn('create.cancel')}
                </Button>
                <Button
                  variant="primary"
                  iconRight={I.arrow}
                  onClick={onSubmit}
                  disabled={!canSubmit}
                  loading={isPending}
                >
                  {tn('create.publish')}
                </Button>
              </Row>
            </Row>
          </Stack>
        </BodyWithAside>
      </ScreenBody>
    </Screen>
  );
}
